import { cloneWithInsurance, isJourneyV2ReturnFares } from 'lib/flights/flightUtils'

export const SYNTHETIC_FARE_FAMILY_ID_PREFIX = 'synthetic-'
export const SYNTHETIC_BASE_FARE_FAMILY_ID = 'synthetic-base'

export function isSyntheticFareFamily(fareFamily?: App.FlightFareFamily): boolean {
  return Boolean(fareFamily?.id.startsWith(SYNTHETIC_FARE_FAMILY_ID_PREFIX))
}

export function getSyntheticAreAllFlightsCovered(fareFamily?: App.FlightFareFamily): boolean {
  return fareFamily?.withEmbeddedInsurance === true
}

function getJourneyFareRulesUrl(journey: App.JourneyV2): string {
  return journey.links.find(link => link.key === 'fareRules')?.value ?? ''
}

// A one-way or Two-One-Ways journey never carries a real `totalRoundTripPrice` - on a departing
// journey the field is its own leg's fare doubled - so it must not be trusted just because it is
// present. Gated the same way useSingleLegFlightTile.ts:35-40 gates the journey side. When there
// are two journeys `addFareFamilyCosts` overwrites these with the true additive sum anyway; the
// guard is what keeps a single-journey synthetic fare from carrying a bogus round-trip total.
function getJourneyFareFamilyCost(journey: App.JourneyV2): App.FlightFareFamilyCost {
  const isReturnFares = isJourneyV2ReturnFares(journey)

  return {
    adult: journey.price.adult.totalFare ?? 0,
    children: journey.price.child?.totalFare ?? 0,
    infant: journey.price.infant?.totalFare ?? 0,
    all: journey.price.all.totalFare,
    totalTax: journey.price.all.totalTax,
    roundTripAdult: isReturnFares ? journey.price.adult.totalRoundTripPrice : undefined,
    roundTripAll: isReturnFares ? journey.price.all.totalRoundTripPrice : undefined,
  }
}

function getJourneyRegularFareFamilyCost(journey: App.JourneyV2): App.FlightFareFamilyCost | undefined {
  if (!journey.regularPrice) return undefined

  const isReturnFares = isJourneyV2ReturnFares(journey)

  return {
    adult: journey.regularPrice.adult.totalFare ?? 0,
    children: journey.regularPrice.child?.totalFare ?? 0,
    infant: journey.regularPrice.infant?.totalFare ?? 0,
    all: journey.regularPrice.all.totalFare,
    totalTax: journey.regularPrice.all.totalTax,
    roundTripAdult: isReturnFares ? journey.regularPrice.adult.totalRoundTripPrice : undefined,
    roundTripAll: isReturnFares ? journey.regularPrice.all.totalRoundTripPrice : undefined,
  }
}

function addFareFamilyCosts(
  left: App.FlightFareFamilyCost,
  right: App.FlightFareFamilyCost,
): App.FlightFareFamilyCost {
  return {
    adult: left.adult + right.adult,
    children: left.children + right.children,
    infant: left.infant + right.infant,
    all: left.all + right.all,
    totalTax: left.totalTax !== undefined && right.totalTax !== undefined ? left.totalTax + right.totalTax : undefined,
    roundTripAdult: left.adult + right.adult,
    roundTripAll: left.all + right.all,
  }
}

function getIncludedBaggage(
  journeys: Array<App.JourneyV2>,
  kind: 'carryOn' | 'checked',
): App.FlightV2BaggageOption | undefined {
  for (const journey of journeys) {
    for (const flight of journey.flightGroup.flights) {
      const baggage = kind === 'carryOn' ? flight.includedCarryOnBaggage : flight.includedCheckedBaggage
      if (baggage?.includedInFare) {
        return baggage
      }
    }
  }

  return undefined
}

function getSyntheticFareFeatures(journeys: Array<App.JourneyV2>): Array<App.FareFamilyFeature> {
  const features: Array<App.FareFamilyFeature> = []
  const carryOn = getIncludedBaggage(journeys, 'carryOn')
  const checked = getIncludedBaggage(journeys, 'checked')

  if (carryOn) {
    features.push({
      status: 'INCLUDED',
      code: carryOn.id || 'carry_on_bag',
      description: carryOn.description,
      type: 'carry_on_bag',
      group: 1,
      category: 'baggage',
    })
  }

  if (checked) {
    features.push({
      status: 'INCLUDED',
      code: checked.id || 'checked_baggage',
      description: checked.description,
      type: 'checked_baggage',
      group: 2,
      category: 'baggage',
    })
  }

  const penaltyStatusPriority: Record<App.FareFamilyFeatureStatus, number> = {
    INCLUDED: 0,
    CHARGED: 1,
    NOT_OFFERED: 2,
  }
  const penaltiesByType = new Map<App.PenaltyInclusion['type'], App.PenaltyInclusion>()

  journeys.flatMap(journey => journey.penaltyInclusions ?? []).forEach((penalty) => {
    const existingPenalty = penaltiesByType.get(penalty.type)
    if (!existingPenalty || penaltyStatusPriority[penalty.status] > penaltyStatusPriority[existingPenalty.status]) {
      penaltiesByType.set(penalty.type, penalty)
    }
  })

  const penaltyGroups: Record<App.PenaltyInclusion['type'], number> = {
    changes: 3,
    refunds: 4,
  }
  for (const type of ['changes', 'refunds'] as const) {
    const penalty = penaltiesByType.get(type)
    if (penalty) {
      features.push({
        ...penalty,
        group: penaltyGroups[type],
        category: 'fare_rules',
      })
    }
  }

  return features
}

interface BuildSyntheticBaseFareFamilyParams {
  journey: App.JourneyV2
  journeysForFeatures: Array<App.JourneyV2>
  id: string
  cost: App.FlightFareFamilyCost
  regularCost?: App.FlightFareFamilyCost
}

function buildSyntheticBaseFareFamily({
  journey,
  journeysForFeatures,
  id,
  cost,
  regularCost,
}: BuildSyntheticBaseFareFamilyParams): App.FlightFareFamily {
  const description = journey.displayName || journey.cabin || journey.name

  return {
    id,
    order: 0,
    cabin: journey.cabin,
    bookingClass: journey.bookingInfo.bookingClass,
    fareRulesUrl: getJourneyFareRulesUrl(journey),
    loyaltyEarnKey: journey.loyaltyEarnKey,
    cost,
    regularCost,
    bookingInfo: {
      isLuxPlusFare: journey.bookingInfo.isLuxPlusFare,
    } as App.FlightFareFamily['bookingInfo'],
    fareFamily: {
      name: description,
      description,
      carrier: journey.carrierName || journey.carrier,
      fareFeatures: getSyntheticFareFeatures(journeysForFeatures),
    },
  }
}

function withSyntheticProtectOption(
  baseFareFamily: App.FlightFareFamily,
  quote: App.InsuranceQuote | undefined,
  occupancies: App.Occupants | undefined,
  shouldDivideQuoteByTwo: boolean,
  useMemberInsurancePrice: boolean,
  isFlightLuxPlusPricingEligible: boolean,
): Array<App.FlightFareFamily> {
  if (!quote) {
    return [baseFareFamily]
  }

  return [
    baseFareFamily,
    cloneWithInsurance(
      baseFareFamily,
      quote,
      occupancies,
      shouldDivideQuoteByTwo,
      undefined,
      useMemberInsurancePrice,
      isFlightLuxPlusPricingEligible,
    ),
  ]
}

export function buildSyntheticDisplayFareFamilies({
  journeys,
  quote,
  occupancies,
  useMemberInsurancePrice,
  isFlightLuxPlusPricingEligible,
}: {
  journeys: Array<App.JourneyV2>
  quote?: App.InsuranceQuote
  occupancies?: App.Occupants
  useMemberInsurancePrice: boolean
  isFlightLuxPlusPricingEligible: boolean
}): Array<App.FlightFareFamily> {
  const primaryJourney = journeys[0]
  if (!primaryJourney) return []

  const cost = journeys.slice(1).reduce(
    (total, journey) => addFareFamilyCosts(total, getJourneyFareFamilyCost(journey)),
    getJourneyFareFamilyCost(primaryJourney),
  )
  const regularCosts = journeys.map(getJourneyRegularFareFamilyCost)
  let regularCost: App.FlightFareFamilyCost | undefined
  if (regularCosts.every(Boolean)) {
    regularCost = regularCosts.slice(1).reduce(
      (total, journeyCost) => addFareFamilyCosts(total!, journeyCost!),
      regularCosts[0],
    )
  }

  const baseFareFamily = buildSyntheticBaseFareFamily({
    journey: primaryJourney,
    journeysForFeatures: journeys,
    id: SYNTHETIC_BASE_FARE_FAMILY_ID,
    cost,
    regularCost,
  })

  return withSyntheticProtectOption(
    baseFareFamily,
    quote,
    occupancies,
    false,
    useMemberInsurancePrice,
    isFlightLuxPlusPricingEligible,
  )
}
